import { useMemo } from 'react';
import produce from 'immer';
import { useQueryClient } from '../../lib';
import { getFeedKey, getPinnedPostsKey, getPostKey, getPostsKey, } from '../../utils/keys';
export const getCachedRootPostByReplies = (id, queryClient) => {
    if (!id)
        return undefined;
    const filter = {
        active: true,
        queryKey: getPostKey(),
    };
    const posts = queryClient.getQueriesData(filter);
    let cachedPost;
    posts.forEach(([_, queryData]) => {
        var _a, _b, _c;
        if (cachedPost)
            return;
        const node = (_a = queryData.replies.nodes) === null || _a === void 0 ? void 0 : _a.find(n => n.id === id);
        if (node) {
            cachedPost = queryData;
            return;
        }
        const edge = (_c = (_b = queryData.replies) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c.map(e => { var _a; return ((_a = e === null || e === void 0 ? void 0 : e.node) === null || _a === void 0 ? void 0 : _a.id) === id; });
        if (edge) {
            cachedPost = queryData;
        }
    });
    return cachedPost;
};
export const getCachedRootPost = (id, queryClient) => {
    const post = getCachedPost(id, queryClient);
    if (post === null || post === void 0 ? void 0 : post.repliedToId)
        return getCachedRootPost(post.repliedToId, queryClient);
    if ((post === null || post === void 0 ? void 0 : post.repliedToId) === null)
        return post;
    return undefined;
};
export const getCachedPost = (id, queryClient) => {
    if (!id)
        return undefined;
    let cachedPost;
    // get cached post
    const postQueries = queryClient.getQueriesData(getPostKey({ variables: { id } }));
    postQueries === null || postQueries === void 0 ? void 0 : postQueries.forEach(query => {
        cachedPost = cachedPost !== null && cachedPost !== void 0 ? cachedPost : query === null || query === void 0 ? void 0 : query[1];
    });
    // get all posts and feed queries
    const postsQueryFilter = {
        predicate: query => {
            var _a;
            const queryMainKey = (_a = query === null || query === void 0 ? void 0 : query.queryKey) === null || _a === void 0 ? void 0 : _a[0];
            if (queryMainKey === getFeedKey()[0] ||
                queryMainKey === getPostsKey()[0] ||
                queryMainKey === getPinnedPostsKey()[0]) {
                return true;
            }
            return false;
        },
    };
    const data = queryClient.getQueriesData(postsQueryFilter);
    data.forEach(([_, queryData]) => {
        var _a;
        if (Array.isArray(queryData)) {
            queryData.forEach(post => {
                var _a, _b;
                if ((post === null || post === void 0 ? void 0 : post.id) === id) {
                    cachedPost = { ...post, ...cachedPost };
                }
                if ((post === null || post === void 0 ? void 0 : post.replies) && !cachedPost) {
                    (_b = (_a = post === null || post === void 0 ? void 0 : post.replies) === null || _a === void 0 ? void 0 : _a.nodes) === null || _b === void 0 ? void 0 : _b.forEach(postReply => {
                        if (postReply.id === id) {
                            cachedPost = { ...postReply, ...cachedPost };
                        }
                    });
                }
            });
            // if queryData is paginated list
        }
        else if (!cachedPost) {
            (_a = queryData === null || queryData === void 0 ? void 0 : queryData.pages) === null || _a === void 0 ? void 0 : _a.forEach(page => {
                var _a;
                (_a = page === null || page === void 0 ? void 0 : page.nodes) === null || _a === void 0 ? void 0 : _a.forEach(post => {
                    var _a, _b;
                    if ((post === null || post === void 0 ? void 0 : post.id) === id) {
                        cachedPost = { ...post, ...cachedPost };
                    }
                    if ((post === null || post === void 0 ? void 0 : post.replies) && !cachedPost) {
                        (_b = (_a = post === null || post === void 0 ? void 0 : post.replies) === null || _a === void 0 ? void 0 : _a.nodes) === null || _b === void 0 ? void 0 : _b.forEach(postReply => {
                            if (postReply.id === id) {
                                cachedPost = { ...postReply, ...cachedPost };
                            }
                        });
                    }
                });
            });
        }
    });
    return cachedPost && Object.keys(cachedPost).length ? cachedPost : null;
};
export const useCachedPost = (id) => {
    const queryClient = useQueryClient();
    return useMemo(() => getCachedPost(id, queryClient), [id, queryClient]);
};
export const addPostToPaginatedPost = (newPost) => (oldPosts) => {
    return produce(oldPosts, draft => {
        var _a, _b;
        if (!(draft === null || draft === void 0 ? void 0 : draft.pages)) {
            draft = {
                pageParams: [],
                pages: [
                    {
                        pageInfo: {
                            hasNextPage: false,
                        },
                        nodes: [],
                        edges: [],
                        totalCount: 0,
                    },
                ],
            };
        }
        (_b = (_a = draft.pages[0]) === null || _a === void 0 ? void 0 : _a.nodes) === null || _b === void 0 ? void 0 : _b.unshift(newPost);
        draft.pages[0].totalCount += 1;
        return draft;
    });
};
export const postRepliesUpdater = (node) => (pn) => {
    var _a, _b, _c;
    return ({
        ...pn,
        repliesCount: (pn === null || pn === void 0 ? void 0 : pn.repliesCount) + 1,
        totalRepliesCount: (pn === null || pn === void 0 ? void 0 : pn.totalRepliesCount) + 1,
        replies: {
            ...pn === null || pn === void 0 ? void 0 : pn.replies,
            totalCount: (((_a = pn === null || pn === void 0 ? void 0 : pn.replies) === null || _a === void 0 ? void 0 : _a.totalCount) || 0) + 1,
            nodes: [node, ...(((_b = pn === null || pn === void 0 ? void 0 : pn.replies) === null || _b === void 0 ? void 0 : _b.nodes) || [])],
            edges: [{ node, cursor: '' }, ...(((_c = pn === null || pn === void 0 ? void 0 : pn.replies) === null || _c === void 0 ? void 0 : _c.edges) || [])],
        },
    });
};
export const infinitePostUpdater = (postId, updater, checkReplies = false) => (oldFeed) => {
    var _a;
    return ({
        ...oldFeed,
        pages: (_a = oldFeed === null || oldFeed === void 0 ? void 0 : oldFeed.pages) === null || _a === void 0 ? void 0 : _a.map(infinitePaginatedPostWithRepliesUpdater(postId, updater, checkReplies)),
    });
};
export const infinitePaginatedPostWithRepliesUpdater = (postId, updater, checkReplies = false) => (posts) => {
    var _a, _b;
    return {
        ...posts,
        nodes: (_a = posts.nodes) === null || _a === void 0 ? void 0 : _a.map(infinitePostArrayUpdater(postId, updater, checkReplies)),
        edges: (_b = posts.edges) === null || _b === void 0 ? void 0 : _b.map(e => {
            let newNode = e.node;
            if (e.node.id === postId) {
                newNode = updater(e.node);
            }
            return {
                ...e,
                node: {
                    ...newNode,
                    ...(checkReplies && {
                        replies: e.node.replies &&
                            infinitePaginatedPostWithRepliesUpdater(postId, updater)(e.node.replies),
                    }),
                },
            };
        }),
    };
};
export const infinitePostArrayUpdater = (postId, updater, checkReplies = false) => (post) => {
    let newNode = post;
    if (post.id === postId) {
        newNode = updater(post);
    }
    return {
        ...newNode,
        ...(checkReplies && {
            replies: post.replies &&
                infinitePaginatedPostWithRepliesUpdater(postId, updater)(post.replies),
        }),
    };
};
export const removeReactionFromPost = (reaction, memberId) => (old) => {
    if (!(old === null || old === void 0 ? void 0 : old.reactions))
        return old;
    const newReactions = old.reactions.reduce((acc, currReaction) => {
        var _a, _b, _c, _d, _e;
        if (currReaction.reaction === reaction) {
            const modifiedReaction = {
                ...currReaction,
                reacted: false,
                count: currReaction.count - 1,
                participants: {
                    ...currReaction.participants,
                    totalCount: ((_a = currReaction.participants) === null || _a === void 0 ? void 0 : _a.totalCount) - 1,
                    nodes: (_c = (_b = currReaction.participants) === null || _b === void 0 ? void 0 : _b.nodes) === null || _c === void 0 ? void 0 : _c.filter(node => { var _a; return ((_a = node.participant) === null || _a === void 0 ? void 0 : _a.id) !== memberId; }),
                    edges: (_e = (_d = currReaction.participants) === null || _d === void 0 ? void 0 : _d.edges) === null || _e === void 0 ? void 0 : _e.filter(({ node }) => { var _a; return ((_a = node.participant) === null || _a === void 0 ? void 0 : _a.id) !== memberId; }),
                },
            };
            if (modifiedReaction.count > 0) {
                acc.push(modifiedReaction);
            }
        }
        else {
            acc.push(currReaction);
        }
        return acc;
    }, []);
    return {
        ...old,
        reactions: newReactions,
        reactionsCount: old.reactionsCount - 1,
    };
};
export const addReactionToPost = (reaction, userReaction) => (old) => {
    var _a, _b, _c, _d;
    if (!(old === null || old === void 0 ? void 0 : old.reactions))
        return old;
    const currentReactionIndex = old.reactions.findIndex(r => r.reaction === reaction);
    let newReaction = {
        participants: {
            edges: [],
            nodes: [],
            pageInfo: {
                endCursor: '',
                hasNextPage: false,
            },
            totalCount: 0,
        },
        reacted: true,
        count: 0,
        reaction,
    };
    if (currentReactionIndex > -1) {
        newReaction = { ...old.reactions[currentReactionIndex] };
    }
    newReaction.reacted = true;
    newReaction.count += 1;
    if (!(newReaction === null || newReaction === void 0 ? void 0 : newReaction.participants)) {
        newReaction.participants = {
            totalCount: 0,
            nodes: [],
            edges: [],
            pageInfo: {
                hasNextPage: false,
            },
        };
    }
    newReaction.participants.totalCount += 1;
    (_b = (_a = newReaction.participants) === null || _a === void 0 ? void 0 : _a.nodes) === null || _b === void 0 ? void 0 : _b.push(userReaction);
    (_d = (_c = newReaction.participants) === null || _c === void 0 ? void 0 : _c.edges) === null || _d === void 0 ? void 0 : _d.push({
        node: userReaction,
        cursor: '',
    });
    const newReactions = currentReactionIndex > -1
        ? old.reactions.map(r => (r.reaction === reaction ? newReaction : r))
        : [newReaction, ...old.reactions];
    return {
        ...old,
        reactions: newReactions,
        reactionsCount: old.reactionsCount + 1,
    };
};
//# sourceMappingURL=useCachedPost.js.map